// @flow
import * as React from 'react'
import { withRouter } from 'react-router-dom'
import { TokenNavLink } from 'catena/er2_ui'
import * as ids from '../constants/ids'
import * as routes from '../constants/routes'
import ficusStyles from '../styles'
import { parseLocation } from '../util/urls'

type Props = {
    location: { pathname: string, search?: string },
    theme: ficusStyles,
    token: { value: string, expires: number },
}

function scrollIntoView(location: { pathname: string, search?: string }) {
    const parsedLoc = parseLocation(location)
    let linkEl
    if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_INTRO)) {
        linkEl = document.getElementById(ids.INSTR_INTRO_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_DESCRIP)) {
        linkEl = document.getElementById(ids.INSTR_DESCRIP_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_USAGE_FRAMEWORKS)) {
        linkEl = document.getElementById(ids.INSTR_USAGE_FRAMEWORKS_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_USAGE_ANALYZE)) {
        linkEl = document.getElementById(ids.INSTR_USAGE_ANALYZE_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_USAGE_BRUSHING)) {
        linkEl = document.getElementById(ids.INSTR_USAGE_BRUSHING_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_USAGE)) {
        linkEl = document.getElementById(ids.INSTR_USAGE_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_BRUSHING)) {
        linkEl = document.getElementById(ids.INSTR_BRUSHING_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_SETTINGS)) {
        linkEl = document.getElementById(ids.INSTR_SETTINGS_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_MAP)) {
        linkEl = document.getElementById(ids.INSTR_MAP_LINK)
    } else if (parsedLoc.path.startsWith(routes.INSTRUCTIONS_REFERENCES)) {
        linkEl = document.getElementById(ids.INSTR_REFERENCES_LINK)
    }

    if (linkEl) linkEl.scrollIntoView()
}

/* eslint-disable react/prefer-stateless-function */
class Instructions extends React.Component<Props> {
    componentDidMount() {
        scrollIntoView(this.props.location)
    }

    componentDidUpdate(prevProps, prevState, prevContext) {
        scrollIntoView(this.props.location)
    }

    render() {
        return (
            <span className={this.props.theme.instructions}>
                <h1>Framework for Integrating Complex Uncertain Systems</h1>
                <h3 id={ids.INSTR_INTRO_LINK}>Introduction and Background</h3>
                <div className={this.props.theme.p}>
                    This is a visualization component prototype for the “Framework for Integrating Complex Uncertain Systems” (FICUS).
                    The purpose of this project is the design and development of a computational framework to support federated models
                    of complex uncertain systems and enable information support for multiple interconnected social, infrastructural, and environmental models.
                    FICUS research goals are to develop a neighborhood scale geo-temporal analysis tool quantifying
                    data and application uncertainty and forecasting near future environmental, social, and infrastructural change.
                </div>
                <div className={this.props.theme.p}>
                    This basic and applied research is funded by OSD-ASA(ALT) and performed by USACE ERDC
                    (POC: <a href="mailto:Charles.R.Ehlschlaeger@usace.army.mil">Charles R. Ehlschlaeger</a>)
                    , Colorado State University
                    (POC: <a href="mailto:odavid@colostate.edu">Olaf David</a>)
                    , and the University of Illinois.
                </div>

                <h3 id={ids.INSTR_DESCRIP_LINK}>Description</h3>
                <div className={this.props.theme.p}>
                    This visualization component allows the geo-temporal animation of indicator maps produced by computational models.
                    Thematic maps are organized in a folder structure and are supplemented with meta-data. The maps can be selected
                    for visualization while uncertainty is spatially animated. For each location, uncertainty information can be obtained
                    and visualized as box plot and histogram. In addition, location based information about realizations can be plotted.
                </div>

                <h3 id={ids.INSTR_USAGE_LINK}>Usage</h3>
                <div>
                    There are 2 steps in this application to follow:
                    <div className={this.props.theme.p}>
                        <div id={ids.INSTR_USAGE_FRAMEWORKS_LINK}><span className={'fa fa-map'} /> Select a Theme</div>
                        <img src="/static/er2_ficus/img/instructions/select_framework.png" className={this.props.theme.graphic} alt="select framework" />
                        <div className={this.props.theme.usageBlock}>
                            Choose a theme to visualize by navigating the <TokenNavLink
                                className={this.props.theme.navButton}
                                to={routes.SELECT_FRAMEWORK}
                                token={this.props.token}
                            >themes tree</TokenNavLink>. Enter text in the search field to highlight nodes of interest. Click on the magnifying
                            glass icon to open matching themes in the tree.
                            Click on the theme&#39;s text to load the theme&#39;s median statistics raster and start the analysis.
                        </div>
                        <div className={this.props.theme.p}>
                            If you have <TokenNavLink
                                className={this.props.theme.navButton}
                                to={routes.SELECT_BOOKMARK}
                                token={this.props.token}
                            >bookmarks</TokenNavLink> defined, you can select your theme from there as well.
                        </div>
                        <div>
                            <h4>Theme Legend</h4>
                            <ul style={{ overflow: 'hidden' }}>
                                <li><span style={{ color: 'red' }}>Red</span> - The theme&#39;s meta.json file contains an error</li>
                                <li><span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>Bold & Italic</span> - The theme contains raster data</li>
                                <li><span style={{ color: 'green' }}>Green</span> - The theme matches the search filter</li>
                            </ul>
                            <img src="/static/er2_ficus/img/instructions/framework_tree.png" style={{ width: '15%', margin: '5px' }} alt="frameworks tree" />
                        </div>
                    </div>
                    <div className={this.props.theme.p}>
                        <div id={ids.INSTR_USAGE_ANALYZE_LINK} style={{ clear: 'both' }}><span className={'fa fa-map-marker'} /> Analyze Data</div>
                        <img src="/static/er2_ficus/img/instructions/analyze_data.png" className={this.props.theme.graphic} alt="analyze data" />
                        <div className={this.props.theme.usageBlock}>
                            Click a location within the <TokenNavLink
                                className={this.props.theme.navButton}
                                to={routes.ANALYZE_FRAMEWORK}
                                token={this.props.token}
                            >map</TokenNavLink> to show the realization values as box plot and histogram.
                            Click on multiple locations to compare them and select the “Realizations” link for each location to plot the realization values.
                            The active location will have a light blue halo around the map popup.
                        </div>
                        <div className={this.props.theme.p}>
                            You can animate the uncertainty map at various speeds and transition manually through the quartiles by using the frame slider
                            or the previous and next buttons.
                        </div>
                    </div>
                    <div className={this.props.theme.p}>
                        <div id={ids.INSTR_USAGE_BRUSHING_LINK} style={{ clear: 'both' }}><span className={'fa fa-paint-brush'} />Brushing and Linking</div>
                        <img src="/static/er2_ficus/img/instructions/brushing.png" className={this.props.theme.graphic} alt="brushing themes" />
                        <div className={this.props.theme.usageBlock}>
                            I describe how to use brushing.
                        </div>
                    </div>
                </div>

                <div style={{ clear: 'both' }} />
                <h3 id={ids.INSTR_USAGE_BRUSHING_LINK}>Brushing & Linking</h3>
                <div className={this.props.theme.p}>
                    <TokenNavLink
                        className={this.props.theme.navButton}
                        to={routes.COLLABORATE}
                        token={this.props.token}
                    >Brushing and Linking</TokenNavLink> is used to share FICUS map interaction and location information among a group of windows.
                    Those windows are linked together in a session. All session members will be able to watch map changes and analytics made in real-time.
                </div>

                <div style={{ clear: 'both' }} />
                <h3 id={ids.INSTR_BRUSHING_LINK}>Brushing and Linking</h3>
                <div className={this.props.theme.p}>
                    Use the <TokenNavLink
                        className={this.props.theme.navButton}
                        to={routes.COLLABORATE}
                        token={this.props.token}
                    >Brushing and Linking</TokenNavLink> page to do brushing and linking.
                </div>

                <div style={{ clear: 'both' }} />
                <h3 id={ids.INSTR_SETTINGS_LINK}>Settings</h3>
                <div className={this.props.theme.p}>
                    Use the <TokenNavLink
                        className={this.props.theme.navButton}
                        to={routes.SETTINGS}
                        token={this.props.token}
                    >Settings</TokenNavLink> page to change:
                    <ul>
                        <li><h4>The transparency of theme layers.</h4></li>
                        <li><h4>The transparency of popup and other map elements.</h4></li>
                        <li><h4>The number of items to store in the most recently used theme bookmarks.</h4></li>
                        <li><h4>The location of the layer legend in the map.</h4></li>
                        <li><h4>Whether layers should be preloaded in the map to speed up layer animation.</h4>
                            If caching is set to true, then all the layers that form the current analysis group will be loaded into the map
                            but turned off. This greatly speeds up the rendering of the layers in the map, but it can also stress
                            the server because of the time and memory required to generate the layer images.
                        </li>
                    </ul>
                </div>

                <div style={{ clear: 'both' }} />
                <h3 id={ids.INSTR_MAP_LINK}>Map Toolbar</h3>
                <div className={this.props.theme.p}>
                    Use the <TokenNavLink
                        className={this.props.theme.navButton}
                        to={routes.MAP}
                        token={this.props.token}
                    >Map Toolbar</TokenNavLink> (located in the upper right corner) icon to change:
                    <ul>
                        <li><h4>The base layer.</h4></li>
                        <li><h4>The map scale.</h4></li>
                    </ul>
                </div>

                <div style={{ clear: 'both' }} />
                <h3 id={ids.INSTR_REFERENCES_LINK}>References</h3>
                <div className={this.props.theme.p}>
                    Ehlschlaeger Charles R., Ashton M. Shortridge, and Michael F. Goodchild, 1997,
                    &quot;Visualizing Spatial Data Uncertainty Using Animation&quot;, Computers in GeoSciences 23(4):387-395.
                </div>
                <div className={this.props.theme.p}>
                    FICUS project resources: <a href="https://alm.engr.colostate.edu/cb/project/FICUS" target="_new">https://alm.engr.colostate.edu/cb/project/FICUS</a>
                </div>
            </span>
        )
    }
}

export default withRouter(Instructions)
